/**
 * PD-16845 — storefront order approval limits.
 *
 * A buyer's `client_public_user.orderApprovalLimit` is the largest order they
 * may place on that account themselves. An order over it cannot be placed by
 * them: it waits as the account's cart, and the account's other buyers whose
 * limit covers it are asked to approve (place) it. This is the legacy shop's
 * behavior, which the SM2 storefront had replaced with silently holding the
 * order in Approval Required.
 *
 * Shared so the cart screen, the submit refusal, the approver list and the
 * stale-stamp migration all apply exactly one rule.
 */

/**
 * The storefront's Awaiting Approval tab (OrdersPage `tab=approval`), where an
 * approver finds the carts they were asked to approve. The approval request
 * email and notification link here.
 */
export const PORTAL_ORDER_APPROVAL_PATH = '/orders?tab=approval';

type DecimalValueModel = string | number | null | undefined;

function toAmount(value: DecimalValueModel): number | null {
	if (value == null) {
		return null;
	}

	const amount = typeof value === 'number' ? value : parseFloat(value);

	return Number.isFinite(amount) ? amount : null;
}

/**
 * Whether an order total is over a buyer's approval limit. A buyer with no
 * limit (null) is never over it, and a total equal to the limit is within it,
 * as the legacy shop compared (`total > limit`). An unreadable limit is
 * treated as no limit, and an unreadable total as $0, so a bad value never
 * strands a cart. Compared in cents so a decimal string and a float that
 * print the same never disagree.
 */
export function isOverApprovalLimit(
	limit: DecimalValueModel,
	total: DecimalValueModel,
): boolean {
	const limitAmount = toAmount(limit);

	if (limitAmount == null) {
		return false;
	}

	const totalAmount = toAmount(total) ?? 0;

	return Math.round(totalAmount * 100) > Math.round(limitAmount * 100);
}

/** Whether a buyer with this limit may place (or approve) an order of this total. */
export function canApproveOrderTotal(
	limit: DecimalValueModel,
	total: DecimalValueModel,
): boolean {
	return !isOverApprovalLimit(limit, total);
}

export interface StorefrontOrderBuyersModel {
	/** `order.fk_public_user__id_submitter`: shown as "Submitted by". */
	submitterId: number;
	/** `order.fk_public_user__id_override`: shown as "Approved by", or null. */
	overrideId: number | null;
}

/**
 * Who a placed storefront order records as its buyers, in the legacy meaning
 * SM1 still displays.
 *
 * On an open cart `fk_public_user__id_submitter` means "this buyer asked for
 * approval" (the storefront no longer stamps it when a cart is created). When
 * another buyer places a requested cart, the requester stays the submitter
 * and the placer is recorded as the approver. When the requester places it
 * themselves (their limit was raised, or the total came down) there is no
 * approver. A cart nobody asked about records whoever placed it.
 */
export function resolveStorefrontOrderBuyers(args: {
	requesterId: number | null | undefined;
	placerId: number;
}): StorefrontOrderBuyersModel {
	if (args.requesterId == null || args.requesterId === args.placerId) {
		return { submitterId: args.placerId, overrideId: null };
	}

	return { submitterId: args.requesterId, overrideId: args.placerId };
}

/**
 * The name a storefront buyer is shown by in approval requests: first and
 * last name, else their email address, else null.
 */
export function portalBuyerDisplayName(
	buyer:
		| {
				firstName?: string | null;
				lastName?: string | null;
				email?: string | null;
		  }
		| null
		| undefined,
): string | null {
	if (buyer == null) {
		return null;
	}

	const name = [buyer.firstName, buyer.lastName]
		.map((part) => part?.trim() ?? '')
		.filter((part) => part !== '')
		.join(' ');

	if (name !== '') {
		return name;
	}

	const email = buyer.email?.trim() ?? '';

	return email !== '' ? email : null;
}
